import { Button } from "@mui/material";
import {
  CustomTable,
  FilterGroupComponent,
  OverflowText,
  SnackbarComponent,
  Title,
} from "components";
import { useGetShareholderList } from "./service/ConfigService";
import { useMemo, useState, useCallback, useEffect } from "react";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";

import { emptyToastMessage } from "consts/common";
import storage from "services/storage";
import { useSnackbar } from "components/snackbar/SnackbarContext";
import CreateShareholderModal from "./components/CreateShareholderModal";
import ViewShareholderModal from "./components/ViewShareholderDetail";
import DeleteShareholderModal from "./components/DeleteShareholder";
import ActivityLog from "./components/ActivityLog";

const ConfigPage = ({ setLayoutAction, setLayoutTitle }) => {
  const { message, setMessage } = useSnackbar();

  const [detailModal, setDetailModal] = useState({ open: false });
  const [deleteModal, setDeleteModal] = useState({ open: false });
  const [createUpdateModal, setCreateUpdateModal] = useState({ open: false });
  const [selected, setSelected] = useState(null);
  const [openActivityLog, setOpenActivityLog] = useState(false);

  const handleOpenCreateModal = useCallback(() => {
    setCreateUpdateModal({ open: true });
  }, []);

  const handleOpenDetailModal = useCallback((_id) => {
    setDetailModal({ open: true });
  }, []);

  const handleOpenDeleteModal = useCallback((_id) => {
    setDeleteModal({ open: true });
  }, []);

  const { response: shareholderListResp, refetch } = useGetShareholderList({
    enableAutoFetchByParams: true,
    params: {
      holding_status: "ACTIVE,INACTIVE",
      is_affiliated: true,
    },
  });

  const handleCloseCreateModal = useCallback(
    async (message) => {
      setCreateUpdateModal({ open: false });
      setMessage(message);
      await refetch();
    },
    [refetch, setMessage],
  );

  const renderActions = useCallback(
    (el) => (
      <div className='flex align-middle gap-2'>
        <RemoveRedEyeOutlinedIcon
          className='cursor-pointer'
          sx={{ color: "#146BDE" }}
          onClick={() => {
            setSelected(el);
            handleOpenDetailModal();
          }}
        />
        {storage.getItem("role")?.includes("ADMIN") && (
          <>
            <EditOutlinedIcon
              className='cursor-pointer'
              sx={{ color: "#FBD603" }}
              onClick={() => {
                setSelected(el);
                handleOpenCreateModal();
              }}
            />
            <DeleteOutlined
              className='cursor-pointer'
              sx={{ color: "#E52A49" }}
              onClick={() => {
                setSelected(el);
                handleOpenDeleteModal();
              }}
            />
          </>
        )}
      </div>
    ),
    [handleOpenCreateModal, handleOpenDetailModal, handleOpenDeleteModal],
  );

  const headCells = [
    {
      id: "sid",
      label: <p className='text-xs'>SID</p>,
      width: "15%",
    },
    {
      id: "name",
      label: <p className='text-xs'>Investor Name</p>,
      sorter: true,
      width: "25%",
    },
    {
      id: "bank_account_number",
      label: <p className='text-xs'>Bank Account Number</p>,
      width: "15%",
    },
    {
      id: "bank_account_holder",
      label: <p className='text-xs'>Bank Account Holder</p>,
      width: "20%",
    },
    {
      id: "actions",
      label: <p className='text-xs'>Actions</p>,
      width: "25%",
    },
  ];

  const bodyCells = useMemo(
    () =>
      shareholderListResp?.data?.shareholders?.map((el) => ({
        ...el,
        name: <OverflowText text={el?.name} maxChar={75} />,
        bank_account_number: el?.bank_account_number || '-',
        bank_account_holder: el?.bank_account_holder || '-',
        actions: renderActions(el),
      })),
    [shareholderListResp, renderActions],
  );

  const handleCloseActivityLog = () => {
    setOpenActivityLog(false);
  };

  // activity log trigger
  useEffect(() => {
    setLayoutTitle(<Title title='Affiliated Shareholders' />);
    setLayoutAction(
      <p
        className='text-[#556FB5] text-xs font-extrabold cursor-pointer'
        onClick={() => setOpenActivityLog(true)}
      >
        Activity Log
      </p>,
    );

    return () => {
      setLayoutTitle();
      setLayoutAction();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='client-management-page section h-full'>
      <div className='flex flex-row mb-5 justify-between'>
        <FilterGroupComponent
          showStatusFilter={false}
          showTypeFilter={false}
          showDateRangePicker={false}
          searchPlaceholder='Search by Exact Name and SID'
          searchParamsQueryKey='search'
        />
        {storage.getItem("role")?.includes("ADMIN") && (
          <Button
            variant='contained'
            onClick={() => {
              setSelected(null);
              handleOpenCreateModal();
            }}
            className='!bg-[#556FB5] text-xs'
          >
            Add Shareholder
          </Button>
        )}
      </div>
      <CustomTable
        id='client-management-list-table'
        headCells={headCells}
        defaultSortByKey='name'
        defaultSortOrder='asc'
        bodyCells={bodyCells ?? []}
        bodyCellKey='name'
        total={shareholderListResp?.data?.total}
      />

      {createUpdateModal?.open && (
        <CreateShareholderModal
          modalProps={{
            open: createUpdateModal?.open,
            onClose: () => {
              setCreateUpdateModal({ open: false });
            },
          }}
          onClose={handleCloseCreateModal}
          existingShareholder={selected}
        />
      )}

      {detailModal?.open && (
        <ViewShareholderModal
          modalProps={{
            open: detailModal?.open,
            onClose: () => {
              setDetailModal({ open: false });
            },
          }}
          onClose={() => {
            setDetailModal({ open: false });
          }}
          existingShareholder={selected}
        />
      )}

      {deleteModal?.open && (
        <DeleteShareholderModal
          modalProps={{
            open: deleteModal?.open,
            onClose: () => {
              setDeleteModal({ open: false });
            },
          }}
          existingShareholder={selected}
          onClose={async (message) => {
            setDeleteModal({ open: false });
            if (message) {
              setMessage(message);
              await refetch();
            }
          }}
        />
      )}

      {message.desc && (
        <SnackbarComponent
          labelTitle={message?.title}
          labelMessage={message?.desc}
          type={message?.type}
          onClose={() => setMessage(emptyToastMessage)}
          duration={5000}
        />
      )}

      {openActivityLog && (
        <ActivityLog
          isOpen={openActivityLog}
          onBack={handleCloseActivityLog}
          onClose={handleCloseActivityLog}
        />
      )}
    </div>
  );
};

export default ConfigPage;
